import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import { api } from './api'
import { ANFANG, beschriftung, periodenname, t } from './sprache'
import { Blattabschnitt } from './Stammdaten'
import type { Beschriftung, Listeneintrag } from './typen'

const KEINE_FASSUNGEN: Listeneintrag[] = []
const KEINE_NAMEN: Record<string, Beschriftung> = {}

/**
 * **Wo eine Position steht, mit jeder Fassung** (7.4 Abschnitt 34.8, R1454).
 *
 * Die Auflage zur Entscheidung vom 2026-10-09: Die Positionsgliederung hat
 * keine eigene Liste mehr (R1453), gepflegt wird im Gliederungsbaum. Was die
 * Liste auf einen Blick zeigte — unter welcher Position, ab wann, bis wann,
 * auch eine eingetragene spätere Umgliederung —, steht seither hier, im Blatt,
 * das der Klick im Baum aufschlägt. Gepflegt wird hier nichts.
 */
export function Positionseinordnung({ position }: { position: string }) {
  // Die Fassungen sind der Inhalt des Abschnitts — ein Hauptabruf (R1211).
  const abruf = useAbruf(
    () =>
      api
        .stammdatenliste('positionsgliederung')
        .then((l) => l.eintraege.filter((e) => e.werte.position === position)),
    [position],
  )
  const fassungen = abruf.wert ?? KEINE_FASSUNGEN

  // Die Namen der übergeordneten Positionen: Nachschlagen im Hintergrund (R1007).
  const namen = useHintergrundabruf<Record<string, Beschriftung>>(
    () =>
      api
        .stammdatenliste('position')
        .then((l) => Object.fromEntries(l.eintraege.map((e) => [e.schluessel, e.namen]))),
    [],
    KEINE_NAMEN,
  )

  const zeilen = [...fassungen].sort((a, b) =>
    (a.werte.gueltig_von ?? '').localeCompare(b.werte.gueltig_von ?? ''),
  )

  return (
    <Blattabschnitt kennung="position.einordnung" titel={t('position.einordnung.titel')}>
      {abruf.ausgefallen && <Abrufausfall abruf={abruf} />}
      {!abruf.laedt && !abruf.ausgefallen && zeilen.length === 0 && (
        <p className="hinweis">{t('position.einordnung.leer')}</p>
      )}
      {zeilen.length > 0 && (
        <ul className="einordnung">
          {zeilen.map((z) => {
            const eltern = z.werte.eltern
            const von = Number(z.werte.gueltig_von)
            const bis = z.werte.gueltig_bis ? Number(z.werte.gueltig_bis) : null

            return (
              <li key={z.schluessel}>
                {eltern
                  ? t('position.einordnung.unter', { eltern: beschriftung(namen[eltern] ?? {}) || eltern })
                  : t('position.einordnung.oberste')}
                {' · '}
                {/* Der Anfang der Zeitachse heißt „von Anfang an“, wie bei den Konten der Position. */}
                {von <= ANFANG ? t('positionskonten.vonanfang') : t('position.einordnung.ab', { periode: periodenname(von) })}
                {bis !== null && (
                  <>
                    {' · '}
                    {t('position.einordnung.bis', { periode: periodenname(bis) })}
                  </>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Blattabschnitt>
  )
}
